import { GetterTree } from 'vuex';
import {
  AppointmentModel,
  AppointmentInfo,
  ClassroomModel,
  EnrollmentModel,
} from '@descomplica/dex-models';
import { AppointmentState } from './state';
import RootState from '@/store/root_state';
import { isGreaterThan, isToday } from '@/helpers/date';
import {
  checkUserEnrollmentAndTaskByPeriodStart,
  isEnrolledInClassroomAfterPeriodStartDate,
} from '@/helpers/users';

const getters: GetterTree<AppointmentState, RootState> = {
  appointmentByClassroomId: (state, appointmentsGetters) => (classroomId: number) => {
    return Object.values(state.appointments)
      .map((appointment: AppointmentInfo) => new AppointmentModel(appointment, appointmentsGetters))
      .filter((appointment) => appointment.classroomId === classroomId);
  },
  appointmentsByUserClassroomsIdsToHappen: (state, appointmentsGetters): AppointmentModel[] => {
    const eventIDs = new Set();

    const { currentUser, currentCourse } = appointmentsGetters;
    const periodStartDate: string = appointmentsGetters.currentPeriodStartDate;

    if (!currentUser || !currentCourse) return [];

    return currentUser
      .getClassroomsByCourseId(currentCourse.id, true)
      .reduce((acc: Array<AppointmentModel>, classroom: ClassroomModel) => {
        classroom.appointments.forEach((appointment: AppointmentModel) => {
          const { event, start, classroomId } = appointment;

          const today: Date = new Date();
          today.setHours(0, 0, 0, 0);

          const resultIsEnrolledInClassroomAfterPeriodStartDate: boolean =
            isToday(periodStartDate) ||
            isEnrolledInClassroomAfterPeriodStartDate(currentUser, periodStartDate, classroomId);
          const resultCheckDates: boolean =
            isGreaterThan(start.toISOString(), today.toISOString()) &&
            resultIsEnrolledInClassroomAfterPeriodStartDate;

          if (event && resultCheckDates && !eventIDs.has(event.id)) {
            acc.push(appointment);
            eventIDs.add(event.id);
          }
        });

        return acc;
      }, [])
      .sort((firstAppointment: AppointmentModel, secondAppointment: AppointmentModel) => {
        const firstAppointmentStartDate: number = new Date(firstAppointment.start).getTime();
        const secondAppointmentStartDate: number = new Date(secondAppointment.start).getTime();

        return firstAppointmentStartDate - secondAppointmentStartDate;
      });
  },
  filteredAppointmentByClassroomId: (state, appointmentsGetters) => (classroomId: number) => {
    const { currentUser } = appointmentsGetters;
    const periodStartDate: string = appointmentsGetters.currentPeriodStartDate;

    if (!currentUser) return [];

    const userClassroomEnrollment: EnrollmentModel | undefined = currentUser
      .getStartedEnrollments()
      .find((enrolllment: EnrollmentModel) => enrolllment.classroom.id === classroomId);

    const appointments: AppointmentModel[] = appointmentsGetters
      .appointmentByClassroomId(classroomId)
      .filter((appointment: AppointmentModel) => {
        return (
          !userClassroomEnrollment ||
          checkUserEnrollmentAndTaskByPeriodStart(
            periodStartDate,
            userClassroomEnrollment.createdAt,
            appointment.start.toISOString(),
          )
        );
      });

    return appointments;
  },
  appointmentsByClassroomIds: (state, appointmentsGetters) => (classroomId: number[]) => {
    return Object.values(state.appointments)
      .filter((appointment) => classroomId.includes(appointment.classroomId))
      .map((appointment) => new AppointmentModel(appointment, appointmentsGetters));
  },
};

export default getters;
